import { Injectable, Injector, OnDestroy } from '@angular/core';

import moment from 'moment-timezone';
import * as uuid from 'uuid';
import { BehaviorSubject, filter, map, mergeMap, Observable, of, Subscription, tap, zip } from 'rxjs';

import { ReportsService } from '../../Common/Reports/services/reports.service';
import { DistributionStatus } from '../../Common/TargetingShared/enum/distribution-status.enum';
import { DistributionService } from '../../Common/TargetingShared/services/distribution.service';
import { CurrentUserStateService } from '../../Core/Users/services/current-user-state.service';
import { TargetingListService } from '../../Discover/Targeting/services/targeting-list.service';
import { TargetingSearchesService } from '../../Discover/Targeting/services/targeting-searches.service';
import { BriefingsService } from '../../Monitor/Briefings/services/briefings.service';
import { FolderService } from '../../Monitor/Core/services/folder.service';
import { TopicsService } from '../../Monitor/Core/services/topics.service';
import { RecentActivities } from '../models/recent-activities.model';
import { MentionSearchCriteriaModel } from '../../Monitor/Shared/models/mention-search-criteria.model';
import { ReportDateRangeModel } from '../../Common/Reports/models/report-date-range.model';
import { DateQueryModel } from '../../Common/Forms/models/date-query.model';
import { UserFeaturesService } from '../../Common/Dashboard/services/user-features.service';
import { NavItemInternal } from '../../Common/Layout/components/Navbar/navbar.component';
import { LANDING_PAGE_FEATURES, NAVBAR_IDS } from '../components/LandingPage/product-features.const';
import { ConfigService } from '../../Core/Config/services/config.service';
import { LandingPageFeatureModule } from '../models/feature-module.model';
import { TargetingListList } from '../../Discover/Targeting/models/targeting-list-list.model';
import { DistributionSummaryModel } from '../../Common/TargetingShared/model/distribution-summary-model';
import { TargetingSearchList } from '../../Discover/Targeting/models/targeting-search-list.model';
import { MentionSearchListItemModel } from '../../Monitor/Shared/models/mention-search-list-item.model';
import { Folder } from '../../Monitor/Shared/models/folder.model';
import { BriefingListItemModel } from '../../Common/MonitorShared/model/briefing-list-item.model';
import { ReportListItemModel } from '../../Common/Reports/models/report-list-item.model';
import { AgentService } from '../../Social/Intelligence/services/agent.service';
import { MediaSubscription } from '../../Monitor/Shared/models/media-subscription.model';
import { AlertsService } from '@Monitor/Alerts/services/alerts.service';

interface ActivityMapperConfig<T>{
    header: string;
    headerIcon: string;
    getTitle: (item: T) => string | undefined;
    getRoute: (item: T) => string;
    getStatus?: (item: T) => string;
    getFilters?: (item: T) => string;
}

@Injectable()
export class LandingPageService implements OnDestroy {

    dateRange: ReportDateRangeModel;

    initialUserFeatures: NavItemInternal[] = [];
    userFeatures: LandingPageFeatureModule[] = [];
    defaultFeatures = LANDING_PAGE_FEATURES;
    sortedFeatures: LandingPageFeatureModule[] = [];
    hasAnyModulePermissions: boolean;

    subscriptions: Subscription[] = [];
    dateFormat: string;

    isReseller: boolean;

    private sortedFeaturesSubject: BehaviorSubject<LandingPageFeatureModule[]> = new BehaviorSubject<LandingPageFeatureModule[]>([]);
    sortedFeatures$ = this.sortedFeaturesSubject.asObservable();

    private isResellerSubject = new BehaviorSubject<boolean>(false);
    isReseller$ = this.isResellerSubject.asObservable();

    private hasAnyModulePermissionsSubject = new BehaviorSubject<boolean>(false);
    hasAnyModulePermissions$ = this.hasAnyModulePermissionsSubject.asObservable();


    private reportsService: ReportsService;
    private briefingsService: BriefingsService;
    private folderService: FolderService;
    private topicsService: TopicsService;
    private distributionService: DistributionService;
    private targetingListService: TargetingListService;
    private targetingSearchesService: TargetingSearchesService;
    private agentService: AgentService;
    private alertsService: AlertsService;

    private readonly recentDistributionConfig: ActivityMapperConfig<DistributionSummaryModel> = {
        header: 'UserPreferences.distributions',
        headerIcon: 'fa-kit-duotone fa-agpr-distribution',
        getTitle: distribution => distribution.name,
        getRoute: distribution =>
            '/Discover/distributions/distribution/' + distribution.id +
            (distribution.status === DistributionStatus.sent ? '/report' : ''),
        getStatus: distribution => distribution.status
    };

    private readonly recentTargetingListConfig: ActivityMapperConfig<TargetingListList> = {
        header: 'UserPreferences.targetedLists',
        headerIcon: 'fa-kit-duotone fa-agpr-targeted-list',
        getTitle: list => list.name,
        getRoute: list => `/Discover/targetinglists/list/${list.id}/results`
    };

    private readonly recentSavedSearchConfig: ActivityMapperConfig<TargetingSearchList> = {
        header: 'UserPreferences.savedSearches',
        headerIcon: 'fa-kit-duotone fa-agpr-saved-search',
        getTitle: search => search.name,
        getRoute: search => `/Discover/targetingsearches/search/${search.id}/results`
    };

    private readonly recentTopicConfig: ActivityMapperConfig<MentionSearchListItemModel> = {
        header: 'UserPreferences.topics',
        headerIcon: 'fa-kit-duotone fa-agpr-topics',
        getTitle: topic => topic.name,
        getRoute: topic => `/Monitoring/topics/topic/${topic.id}`
    };

    private readonly recentSavedCoverageConfig: ActivityMapperConfig<Folder> = {
        header: 'Monitor.leftSideBar.savedMentions.savedMentions',
        headerIcon: 'fa-kit-duotone fa-agpr-folders',
        getTitle: savedCoverage => savedCoverage.name,
        getRoute: savedCoverage => `/Monitoring/mentions/folder/${savedCoverage.id}`
    };

    private readonly recentBriefingConfig: ActivityMapperConfig<BriefingListItemModel> = {
        header: 'UserPreferences.briefings',
        headerIcon: 'fa-kit-duotone fa-agpr-briefings',
        getTitle: briefing => briefing.name,
        getRoute: briefing => `/Monitoring/briefings/briefing/${briefing.id}`,
        getStatus: briefing => briefing.status
    };

    private readonly recentReportConfig: ActivityMapperConfig<ReportListItemModel> = {
        header: 'UserPreferences.reports',
        headerIcon: 'fa-kit-duotone fa-agpr-reports',
        getTitle: report => report.name,
        getRoute: report => `/Report/reports/report/${report.id}`
    };

    private readonly recentSocialActivityConfig: ActivityMapperConfig<MediaSubscription> = {
        header: 'Core.navigation.social',
        headerIcon: 'fa-kit-duotone fa-agpr-social',
        getTitle: agent => agent.name,
        getRoute: agent =>
            agent.themeIds?.[0]
                ? `/Social/themes/theme/${agent.themeIds[0]}/postFeed` : '/Social',
        getFilters: agent => {
            const filters = new MentionSearchCriteriaModel();
            filters.query.agent.include = [agent.id];
            filters.query.dateQuery = DateQueryModel.clone(this.dateRange);

            const uuidValue = uuid.v4();
            return MentionSearchCriteriaModel.toFilterQueryString(filters, uuidValue);
        }
    };

    private readonly recentAlertConfig: ActivityMapperConfig<BriefingListItemModel> = {
        header: 'UserPreferences.coverageAlerts',
        headerIcon: 'fa-duotone fa-regular fa-bell-exclamation',
        getTitle: alert => alert.name,
        getRoute: alert => `/Monitoring/alerts/alert/${alert.id}`,
        getStatus: alert => alert.status
    };

    constructor(
        private injector: Injector,
        private currentUserStateService: CurrentUserStateService,
        private userFeaturesService: UserFeaturesService,
        private configService: ConfigService
    ) {
        this.dateRange = new ReportDateRangeModel();
    }

    initializeDataServices(): void {
        this.reportsService = this.injector.get(ReportsService);
        this.briefingsService = this.injector.get(BriefingsService);
        this.folderService = this.injector.get(FolderService);
        this.topicsService = this.injector.get(TopicsService);
        this.distributionService = this.injector.get(DistributionService);
        this.targetingListService = this.injector.get(TargetingListService);
        this.targetingSearchesService = this.injector.get(TargetingSearchesService);
        this.agentService = this.injector.get(AgentService);
        this.alertsService = this.injector.get(AlertsService);
    }

    loadAllRecentActivities(numOfItems: number, userId?: number): Observable<RecentActivities[]> {
        return zip([
            this.prepareRecentDistributionActivityData(numOfItems, userId),
            this.prepareRecentData(
                this.targetingListService.all$, numOfItems, userId, NAVBAR_IDS.MEDIA_RELATIONS,
                NAVBAR_IDS.TARGETED_LISTS, this.recentTargetingListConfig
            ),
            this.prepareRecentData(
                this.targetingSearchesService.all$, numOfItems, userId, NAVBAR_IDS.MEDIA_RELATIONS,
                NAVBAR_IDS.SAVED_SEARCHES, this.recentSavedSearchConfig
            ),
            this.prepareRecentData(
                this.topicsService.getTopics(), numOfItems, userId,
                NAVBAR_IDS.MEDIA_INTELLIGENCE, NAVBAR_IDS.TOPICS, this.recentTopicConfig
            ),
            this.prepareRecentData(
                this.folderService.all$, numOfItems, userId,
                NAVBAR_IDS.MEDIA_INTELLIGENCE, NAVBAR_IDS.MENTIONS, this.recentSavedCoverageConfig
            ),
            this.prepareRecentData(
                this.briefingsService.getBriefingListItems(), numOfItems, userId,
                NAVBAR_IDS.MEDIA_INTELLIGENCE, NAVBAR_IDS.BRIEFINGS, this.recentBriefingConfig
            ),
            this.prepareRecentData(
                this.reportsService.getReportListItems(), numOfItems, userId,
                NAVBAR_IDS.MEDIA_INTELLIGENCE, NAVBAR_IDS.REPORTS, this.recentReportConfig
            ),
            this.prepareRecentData(
                this.agentService.all$, numOfItems, userId, NAVBAR_IDS.SOCIAL,
                NAVBAR_IDS.SOCIAL_LISTENING, this.recentSocialActivityConfig
            ),
            this.prepareRecentData(
                this.alertsService.getAlertsList(), numOfItems, userId,
                NAVBAR_IDS.MEDIA_INTELLIGENCE, NAVBAR_IDS.ALERTS, this.recentAlertConfig
            )
        ]).pipe(
            mergeMap(([distributions, lists, savedSearches, topics, savedCoverages, briefings, reports, socialTopics, alerts]) => {

                const allRecentActivities: RecentActivities[] = [
                    ...distributions,
                    ...lists,
                    ...savedSearches,
                    ...topics,
                    ...savedCoverages,
                    ...briefings,
                    ...reports,
                    ...socialTopics,
                    ...alerts
                ];

                numOfItems = Math.min(allRecentActivities.length, numOfItems);
                let result: RecentActivities[] = [];

                if (userId) {
                    result = allRecentActivities.sort(this.sortByUserActivityDate(userId)).slice(0, numOfItems);
                } else {
                    result = allRecentActivities.sort(this.sortByDate).slice(0, numOfItems);
                }

                return this.currentUserStateService.getPreferences().pipe(
                    filter(x => !!x),
                    map(prefs => {
                        this.formatActivityDatesForTimezone(result, prefs.timeZone);
                        return result;
                    })
                );
            })
        );
    }
    setupSubscriptions(): void {
        this.isReseller = this.configService.hasBrandGUID();
        this.isResellerSubject.next(this.isReseller);
        this.subscriptions.push(
            this.currentUserStateService.getPreferences().pipe(filter(x => !!x)).subscribe(prefs => {
                this.dateFormat = prefs.getDateFormatString(true);
            }),
            this.userFeaturesService.userFeatures$
                .pipe(filter(features => features.length > 0))
                .subscribe(features => {
                    this.initialUserFeatures = features;

                    if (this.isReseller) {
                        this.userFeatures = this.mapNavModulesForWhiteLabel(features);
                    } else {
                        this.userFeatures = this.markPermissions(this.defaultFeatures, features);
                    }
                    this.sortedFeatures = this.sortFeatures(this.userFeatures);
                    this.sortedFeaturesSubject.next(this.sortedFeatures);
                    this.checkModulePermissions();
                })
        );
    }
    /**
     * Sorts an list by modified date (or created date, if modified date is not available)
     */
    private sortByDate(a, b): number {
        return (+b.modifiedDate || +b.createdDate) - (+a.modifiedDate || +a.createdDate);
    }

    /**
     * Returns a comparator to sort RecentActivities for a specific user
     */
    private sortByUserActivityDate(userId: number) {
        return (a, b) => {
            const aDate = (a.modifiedByUserId === userId && a.modifiedDate) || (a.createdByUserId === userId && a.createdDate);
            const bDate = (b.modifiedByUserId === userId && b.modifiedDate) || (b.createdByUserId === userId && b.createdDate);
            return (+bDate) - (+aDate);
        };
    }

    prepareRecentDistributionActivityData(
        numOfItems: number,
        userId?: number
    ): Observable<RecentActivities[]> {

        return this.prepareRecentData(
            this.distributionService.getDistributionSummaries(),
            numOfItems,
            userId,
            NAVBAR_IDS.MEDIA_RELATIONS,
            NAVBAR_IDS.DISTRIBUTIONS,
            this.recentDistributionConfig
        ).pipe(
            mergeMap(recentDistributions => {

                const scheduledIds = recentDistributions
                    .filter(distribution => distribution.status === DistributionStatus.scheduled)
                    .map(distribution => +distribution.id);

                if (scheduledIds.length === 0) {
                    return of(recentDistributions);
                }

                return this.distributionService.getDistributionByIds(scheduledIds).pipe(
                    map(distributions => {

                        const scheduledMap = new Map(
                            distributions.map(distribution => [distribution.id, distribution])
                        );

                        recentDistributions.forEach(item => {
                            const fullDistribution = scheduledMap.get(+item.id);
                            if (fullDistribution?.scheduledDate) {
                                item.date = +fullDistribution.scheduledDate;
                            }
                        });

                        return recentDistributions;
                    })
                );
            })
        );
    }

    private mapItemToActivity<T>(
        item: T,
        userId: number | undefined,
        config: ActivityMapperConfig<T>
    ): RecentActivities {
        const createdDate = (item as any).createdDate ? (item as any).createdDate.getTime() : null;
        const modifiedDate = (item as any).modifiedDate ? (item as any).modifiedDate.getTime() : null;

        let activityDate = modifiedDate ?? createdDate;
        let status = config.getStatus ? config.getStatus(item) : modifiedDate ? 'Modified' : 'Created';

        if (userId) {
            if ((item as any).modifiedByUserId === userId) {
                activityDate = modifiedDate;
                status = config.getStatus ? config.getStatus(item) : 'Modified';
            } else if ((item as any).createdByUserId === userId && createdDate) {
                activityDate = createdDate;
                status = config.getStatus ? config.getStatus(item) : 'Created';
            }
        }

        return {
            id: (item as any).id?.toString(),
            header: config.header,
            headerIcon: config.headerIcon,
            title: config.getTitle(item),
            date: activityDate,
            createdByUserId: (item as any).createdByUserId,
            modifiedByUserId: (item as any).modifiedByUserId,
            createdDate,
            modifiedDate,
            route: config.getRoute(item),
            status: status,
            filters: config.getFilters?.(item)
        };
    }

    private prepareRecentData<T>(
        source$: Observable<T[]>,
        numOfItems: number,
        userId: number | undefined,
        featureModuleId: string,
        featureId: string,
        mapperConfig: ActivityMapperConfig<T>
    ): Observable<RecentActivities[]> {

        if (!this.hasFeaturePermission(featureModuleId, featureId)) {
            return of([]);
        }

        let recentActivities: RecentActivities[] = [];

        return source$.pipe(
            filter(x => !!x),
            map(items => {
                numOfItems = Math.min(numOfItems, items.length);
                let activities = items.map(item => this.mapItemToActivity(item, userId, mapperConfig));

                if (userId) {
                    activities = activities
                        .filter(activity => activity.modifiedByUserId === userId || activity.createdByUserId === userId)
                        .sort(this.sortByUserActivityDate(userId));
                } else {
                    activities.sort(this.sortByDate);
                }

                recentActivities = activities.slice(0, numOfItems);
                return recentActivities;
            })
        );
    }


    private formatActivityDatesForTimezone(data: RecentActivities[], timeZone: string): void {
        data.forEach(item => {

            // convert createdDate
            if (item.createdDate) {
                item.createdDate = moment(item.createdDate).tz(timeZone).format(this.dateFormat);
            }

            // convert modifiedDate
            if (item.modifiedDate) {
                item.modifiedDate = moment(item.modifiedDate).tz(timeZone).format(this.dateFormat);
            }

            // convert sentDate
            if (item.sentDate) {
                item.sentDate = moment(item.sentDate).tz(timeZone).format(this.dateFormat);
            }

            // convert Date
            if (item.date) {
                item.date = moment(item.date).tz(timeZone).format(this.dateFormat);
            }
        });
    }

    private hasFeaturePermission(moduleId: string, featureId: string): boolean {
        if (!this.initialUserFeatures || this.initialUserFeatures.length === 0) {
            return false;
        }
        const module = this.initialUserFeatures.find(m => m.id === moduleId);
        if (!module || module.showUpsell) {
            return false;
        }

        const feature = module.items?.find(item => item.id === featureId);
        if (!feature || feature.showUpsell) {
            return false;
        }
        return true;
    }

    private mapNavModulesForWhiteLabel(navModules: NavItemInternal[]): LandingPageFeatureModule[] {
        return navModules
            .map(nav => {
                const defaultModule = LANDING_PAGE_FEATURES.find(m => m.id === nav.id);
                if (!defaultModule) {
                    return null;
                };

                return {
                    ...defaultModule,
                    hasPermission: !nav.showUpsell,
                    items: defaultModule.items
                        .map(item => {
                            const navItem = nav.items?.find(i => i.id === item.id);
                            return {
                                ...item,
                                hasPermission: navItem ? !navItem.showUpsell : false
                            };
                        })
                        .filter(item => item.hasPermission)
                };
            })
            .filter(m => m !== null);
    }

    private markPermissions(availableFeatures: LandingPageFeatureModule[], navFeatures: NavItemInternal[]) {
        return availableFeatures.map(module => {
            const featureModule = navFeatures.find(feature => feature.id === module.id);
            const mappedItems = module.items.map(item => {
                const featureItem = featureModule?.items?.find(subItem => subItem.id === item.id);
                return { ...item, hasPermission: !!featureItem };
            });

            return {
                ...module,
                hasPermission: !!featureModule && !featureModule.showUpsell,
                items: this.sortFeatures(mappedItems)
            };
        });
    }

    private sortFeatures(features: any[]): any[] {
        return features.sort((a, b) => {
            return Number(b.hasPermission) - Number(a.hasPermission);
        });
    }

    private checkModulePermissions(): void {
        this.hasAnyModulePermissions = this.userFeatures.some(feature => feature.hasPermission);
        this.hasAnyModulePermissionsSubject.next(this.hasAnyModulePermissions);
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
